import React from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Product } from './types';
import { SdsDropzone } from '../products/SdsDropzone';

interface ProductSdsCardProps {
  open: boolean;
  product: Product;
  canAddSds: boolean;
  canReplaceSds: boolean;
  canDeleteSds: boolean;
  uploadLoading: boolean;
  downloadLoading: boolean;
  deleteLoading: boolean;
  error?: string | null;
  warning?: string | null;
  onClose: () => void;
  onUpload: (file: File, extractLabelTextFromClp: boolean) => void | Promise<void>;
  onDownload: () => void | Promise<void>;
  onDelete: () => void | Promise<void>;
}

export const ProductSdsCard: React.FC<ProductSdsCardProps> = ({
  open,
  product,
  canAddSds,
  canReplaceSds,
  canDeleteSds,
  uploadLoading,
  downloadLoading,
  deleteLoading,
  error,
  warning,
  onClose,
  onUpload,
  onDownload,
  onDelete,
}) => {
  const { t } = useTranslation();
  const attachedSds = product.sds;

  const canUpload = (!attachedSds && canAddSds) || (attachedSds && canReplaceSds);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={(theme) => ({
        zIndex: theme.zIndex.modal,
        '& .MuiBackdrop-root': {
          zIndex: theme.zIndex.modal - 1,
        },
        '& .MuiDrawer-paper': {
          zIndex: theme.zIndex.modal,
          borderRadius: 0,
        },
      })}
    >
      <Box
        role="dialog"
        aria-label={t('productDetails.sdsManageTitle')}
        sx={{
          width: { xs: '100vw', sm: 460 },
          maxWidth: '100vw',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ px: 3, py: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {t('productDetails.sdsManageTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('productDetails.sdsManageDescription')}
            </Typography>
          </Box>
          <IconButton aria-label={t('productDetails.close')} onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack spacing={2.5} sx={{ p: 3, overflowY: 'auto' }}>
          <Stack
            spacing={1.5}
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.default',
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <DescriptionIcon color={attachedSds ? 'primary' : 'action'} />
              <Box minWidth={0}>
                <Typography variant="subtitle2" fontWeight={700}>
                  {attachedSds ? t('productDetails.sdsCurrentFile') : t('productDetails.sdsNotAttached')}
                </Typography>
                {attachedSds && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ overflowWrap: 'anywhere' }}
                  >
                    {attachedSds.original_filename}
                  </Typography>
                )}
              </Box>
            </Stack>

            {attachedSds && (
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                disabled={downloadLoading}
                onClick={() => void onDownload()}
                sx={{ alignSelf: 'flex-start' }}
              >
                {t('productDetails.sdsDownload')}
              </Button>
            )}
          </Stack>

          {canUpload ? (
            <SdsDropzone
              title={attachedSds ? t('productDetails.sdsReplaceTitle') : t('productDetails.sdsAddTitle')}
              buttonLabel={attachedSds ? t('productDetails.sdsReplaceAction') : t('productDetails.sdsAddAction')}
              helperText={t('products.sdsHelper')}
              extractLabelTextSwitchLabel={t('productDetails.sdsExtractLabelTextSwitch')}
              currentFileName={attachedSds?.original_filename}
              loading={uploadLoading}
              error={error}
              warning={warning}
              onFileSelected={onUpload}
            />
          ) : (
            <Stack spacing={1}>
              {error && <Alert severity="error">{error}</Alert>}
              <Alert severity="info">{t('productDetails.sdsNoUploadPermission')}</Alert>
            </Stack>
          )}

          {attachedSds && canDeleteSds && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  {t('productDetails.sdsDangerZoneTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t('productDetails.sdsDangerZoneDescription')}
                </Typography>
                <Button
                  color="error"
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  disabled={deleteLoading}
                  onClick={() => void onDelete()}
                >
                  {t('productDetails.sdsDelete')}
                </Button>
              </Box>
            </>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
};
